/**
 * Whether the app is in the foreground, as a subscribable fact.
 *
 * Deliberately not analytics-specific, and deliberately separate from
 * `onNextResume` in `lib/capacitor.ts`, which fires once and removes itself. Three
 * things need a *persistent* listener: the dwell timer has to stop counting when the
 * app is backgrounded, the event queue has to flush before the OS may kill the
 * process, and a session has to expire while nobody is looking.
 *
 * Native uses Capacitor's `appStateChange`, which is the only signal that fires when
 * the app leaves the foreground on iOS. The web falls back to `visibilitychange`,
 * plus `pagehide` for the case where the tab is going away entirely.
 */

import { App } from "@capacitor/app";

import { isNative } from "~/lib/capacitor";

type Listener = (active: boolean) => void;

const listeners = new Set<Listener>();
let active = true;
let started = false;

function publish(next: boolean): void {
  if (next === active) return;
  active = next;
  for (const listener of listeners) {
    try {
      listener(next);
    } catch {
      // One bad subscriber must not stop the others from being told.
    }
  }
}

/** Whether the app is in the foreground right now. */
export function isAppActive(): boolean {
  return active;
}

/**
 * Subscribe to foreground/background transitions. Returns an unsubscribe function.
 *
 * Safe to call before `initializeAppState`; the listener simply hears nothing until
 * the shell is wired up.
 */
export function onAppStateChange(listener: Listener): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/**
 * Start listening. Returns a disposer, matching `initializeNativeShell`.
 *
 * Idempotent: a second call while already started is a no-op, so React's double
 * effect invocation in development cannot stack two sets of listeners.
 */
export async function initializeAppState(): Promise<() => void> {
  if (started) return () => undefined;
  started = true;

  if (!isNative()) {
    const onVisibility = () => publish(document.visibilityState === "visible");
    const onHide = () => publish(false);
    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("pagehide", onHide);
    active = document.visibilityState === "visible";
    return () => {
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("pagehide", onHide);
      started = false;
    };
  }

  const handle = await App.addListener("appStateChange", ({ isActive }) => {
    publish(isActive);
  });
  return () => {
    void handle.remove();
    started = false;
  };
}

/** Reset module state. Tests only. */
export function resetAppState(): void {
  listeners.clear();
  active = true;
  started = false;
}
